import { Box, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { FC, memo, useMemo, useState } from 'react';
import { buildYamlLines } from './yamlLines';

const useStyles = makeStyles(
  theme => ({
    root: { display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 },
    pathBar: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      minHeight: theme.spacing(7),
    },
    pathValue: {
      fontFamily: 'monospace',
      fontWeight: 600,
      wordBreak: 'break-all',
    },
    code: {
      margin: 0,
      padding: theme.spacing(2, 0),
      overflow: 'auto',
      flex: 1,
      fontFamily: 'monospace',
      fontSize: 13,
      lineHeight: 1.7,
    },
    line: {
      display: 'block',
      padding: theme.spacing(0, 2),
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      borderLeft: '3px solid transparent',
    },
    selectable: { cursor: 'pointer' },
    active: {
      backgroundColor: theme.palette.action.hover,
      borderLeftColor: theme.palette.primary.main,
    },
    punctuation: { color: theme.palette.text.secondary },
    key: { color: theme.palette.primary.main, fontWeight: 600 },
    string: { color: theme.palette.success.main },
    number: { color: theme.palette.warning.main },
    boolean: { color: theme.palette.info.main },
    null: { color: theme.palette.text.secondary, fontStyle: 'italic' },
  }),
  { name: 'YamlPathViewer' },
);

export interface YamlPathViewerProps {
  data: unknown;
  ariaLabel: string;
  onSelectPath: (path: string) => void;
}

export const YamlPathViewer: FC<YamlPathViewerProps> = memo(
  ({ data, ariaLabel, onSelectPath }) => {
    const classes = useStyles();
    const lines = useMemo(() => buildYamlLines(data), [data]);
    const [activeIndex, setActiveIndex] = useState<number>();
    const activeLine =
      activeIndex === undefined ? undefined : lines[activeIndex];

    return (
      <Box className={classes.root}>
        <Box className={classes.pathBar} aria-live="polite">
          <Typography variant="body1" color="textSecondary" component="div">
            {activeLine?.selectable
              ? 'Click to use this path'
              : 'Hover a field to see its path, click a value to use it'}
          </Typography>
          <Typography variant="body2" className={classes.pathValue}>
            {activeLine?.path ?? '\u00a0'}
          </Typography>
        </Box>
        <Box
          component="pre"
          className={classes.code}
          aria-label={ariaLabel}
          onMouseLeave={() => setActiveIndex(undefined)}
        >
          {lines.map((line, index) => (
            <span
              // Lines have no stable identity beyond their position.
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              role={line.selectable ? 'button' : undefined}
              tabIndex={line.selectable ? 0 : undefined}
              aria-label={line.selectable ? `Use path ${line.path}` : undefined}
              className={clsx(
                classes.line,
                line.selectable && classes.selectable,
                activeIndex === index && classes.active,
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onClick={() => line.selectable && onSelectPath(line.path)}
              onKeyDown={event => {
                if (
                  line.selectable &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault();
                  onSelectPath(line.path);
                }
              }}
            >
              <span className={classes.punctuation}>{line.prefix}</span>
              {line.key !== undefined && (
                <>
                  <span className={classes.key}>{line.key}</span>
                  <span className={classes.punctuation}>:</span>
                </>
              )}
              {line.value !== undefined && (
                <>
                  {line.key !== undefined && ' '}
                  <span className={line.kind ? classes[line.kind] : undefined}>
                    {line.value}
                  </span>
                </>
              )}
            </span>
          ))}
        </Box>
      </Box>
    );
  },
);
